import React, { useCallback, useEffect, useRef, useState } from "react";
import { Clock, Search, SlidersHorizontal, Ticket, X } from "lucide-react";
import {
  PROGRAM_SORT_OPTIONS,
  PROGRAM_TIME_FILTERS,
} from "../../utils/bettingDeskHelpers";

const ProgramFilters = ({
  search = "",
  onSearchChange,
  timeFilter,
  onTimeFilterChange,
  leagueId,
  onLeagueChange,
  leagues = [],
  timeCounts = {},
  onSlipOnly,
  onSlipOnlyChange,
  slipCount = 0,
  startingSoon,
  onStartingSoonChange,
  sort,
  onSortChange,
  onClear,
  showClear = false,
  variant = "header",
}) => {
  const rootClass =
    variant === "search"
      ? "pbd-chrome-toolbar pbd-chrome-toolbar-search"
      : variant === "header"
        ? "pbd-chrome-toolbar"
        : "pbd-program-dock";

  const inputRef = useRef(null);
  const searchWrapRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);

  const hasQuery = Boolean(String(search || "").trim());
  const isExpanded = searchOpen || hasQuery;

  const openSearch = useCallback(() => {
    setSearchOpen(true);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    onSearchChange?.("");
  }, [onSearchChange]);

  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const handlePointerDown = (event) => {
      if (searchWrapRef.current?.contains(event.target)) return;
      if (!hasQuery) setSearchOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (hasQuery) {
        onSearchChange?.("");
      } else {
        setSearchOpen(false);
      }
      inputRef.current?.blur();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isExpanded, hasQuery, onSearchChange]);

  const handleBlur = () => {
    window.setTimeout(() => {
      if (!String(search || "").trim() && !searchWrapRef.current?.contains(document.activeElement)) {
        setSearchOpen(false);
      }
    }, 120);
  };

  if (variant === "search") {
    return (
      <div className={rootClass} role="toolbar" aria-label="Search fixtures">
        <div
          ref={searchWrapRef}
          className={`pbd-chrome-search ${isExpanded ? "pbd-chrome-search-open" : ""} ${
            hasQuery ? "pbd-chrome-search-active" : ""
          }`}
        >
          {!isExpanded ? (
            <button
              type="button"
              className="pbd-chrome-search-toggle"
              onClick={openSearch}
              aria-label="Search fixtures"
              aria-expanded={false}
            >
              <Search size={14} strokeWidth={2.3} aria-hidden />
            </button>
          ) : (
            <>
              <Search size={13} aria-hidden className="pbd-chrome-search-icon" />
              <input
                ref={inputRef}
                type="search"
                className="pbd-chrome-search-input"
                placeholder="Search teams or league…"
                value={search}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onBlur={handleBlur}
                aria-label="Search fixtures"
                aria-expanded
              />
              {hasQuery ? (
                <button
                  type="button"
                  className="pbd-chrome-search-clear"
                  onClick={() => onSearchChange?.("")}
                  aria-label="Clear search"
                >
                  <X size={12} />
                </button>
              ) : (
                <button
                  type="button"
                  className="pbd-chrome-search-clear"
                  onClick={closeSearch}
                  aria-label="Close search"
                >
                  <X size={12} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass} role="toolbar" aria-label="Program filters">
      <div className="pbd-chrome-filter-rail">
        <div className="pbd-chrome-filter-group" role="group" aria-label="Kickoff window">
          {PROGRAM_TIME_FILTERS.map(({ id, label }) => {
            const count = timeCounts[id];
            const active = timeFilter === id;
            return (
              <button
                key={id}
                type="button"
                className={`pbd-filter-chip pbd-filter-time-chip ${active ? "pbd-filter-chip-active" : ""}`}
                aria-pressed={active}
                onClick={() => onTimeFilterChange?.(id)}
              >
                <span>{label}</span>
                {typeof count === "number" ? (
                  <span className="pbd-filter-chip-count tabular-nums">{count}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        <span className="pbd-chrome-filter-divider" aria-hidden />

        <label className="pbd-filter-select-wrap">
          <SlidersHorizontal size={11} aria-hidden />
          <select
            className="pbd-filter-select"
            value={leagueId}
            onChange={(e) => onLeagueChange?.(e.target.value)}
            aria-label="Filter by league"
          >
            <option value="">All leagues</option>
            {leagues.map((league) => (
              <option key={league.id} value={league.id}>
                {league.name} ({league.count})
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className={`pbd-filter-chip pbd-filter-toggle ${startingSoon ? "pbd-filter-chip-active" : ""}`}
          aria-pressed={startingSoon}
          onClick={() => onStartingSoonChange?.(!startingSoon)}
        >
          <Clock size={11} aria-hidden />
          <span>Soon</span>
        </button>

        <button
          type="button"
          className={`pbd-filter-chip pbd-filter-toggle ${onSlipOnly ? "pbd-filter-chip-active" : ""}`}
          aria-pressed={onSlipOnly}
          disabled={slipCount === 0}
          onClick={() => onSlipOnlyChange?.(!onSlipOnly)}
        >
          <Ticket size={11} aria-hidden />
          <span>Slip</span>
          {slipCount > 0 ? (
            <span className="pbd-filter-chip-count tabular-nums">{slipCount}</span>
          ) : null}
        </button>

        <label className="pbd-filter-select-wrap pbd-filter-select-wrap-sort">
          <select
            className="pbd-filter-select"
            value={sort}
            onChange={(e) => onSortChange?.(e.target.value)}
            aria-label="Sort fixtures"
          >
            {PROGRAM_SORT_OPTIONS.map(({ id, label }) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>

        {showClear ? (
          <button type="button" className="pbd-filter-clear" onClick={onClear}>
            <X size={11} aria-hidden />
            <span>Reset</span>
          </button>
        ) : null}
      </div>

      <div
        ref={searchWrapRef}
        className={`pbd-chrome-search ${isExpanded ? "pbd-chrome-search-open" : ""} ${
          hasQuery ? "pbd-chrome-search-active" : ""
        }`}
      >
        {!isExpanded ? (
          <button
            type="button"
            className="pbd-chrome-search-toggle"
            onClick={openSearch}
            aria-label="Search fixtures"
            aria-expanded={false}
          >
            <Search size={14} strokeWidth={2.3} aria-hidden />
          </button>
        ) : (
          <>
            <Search size={13} aria-hidden className="pbd-chrome-search-icon" />
            <input
              ref={inputRef}
              type="search"
              className="pbd-chrome-search-input"
              placeholder="Search teams or league…"
              value={search}
              onChange={(e) => onSearchChange?.(e.target.value)}
              onBlur={handleBlur}
              aria-label="Search fixtures"
              aria-expanded
            />
            {hasQuery ? (
              <button
                type="button"
                className="pbd-chrome-search-clear"
                onClick={() => onSearchChange?.("")}
                aria-label="Clear search"
              >
                <X size={12} />
              </button>
            ) : (
              <button
                type="button"
                className="pbd-chrome-search-clear"
                onClick={closeSearch}
                aria-label="Close search"
              >
                <X size={12} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProgramFilters;
